// Story 1.21 — TanStack mutation for POST /api/v1/creators/image-upload-url.
//
// Returns a SAS URI + required headers + the canonical blob_path so
// the browser can PUT the file directly to Azure. Fresh idempotency
// key per call so SPA-side retries are safe.

import { useMutation } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export type MintImageUploadUrlRequest = {
  filename: string;
  content_length: number;
  content_type: string;
};

export type MintImageUploadUrlResponse = {
  upload_url: string;
  blob_path: string;
  expires_at: string;
  required_headers: {
    "x-ms-blob-type": string;
    "x-ms-blob-content-type": string;
  };
};

export type MintImageUploadUrlError = {
  code: string;
  message: string;
  reason?: string;
};

async function postMint(
  body: MintImageUploadUrlRequest,
  idempotencyKey: string,
): Promise<MintImageUploadUrlResponse> {
  const response = await fetch("/api/v1/creators/image-upload-url", {
    method: "POST",
    credentials: "include",
    headers: {
      "content-type": "application/json",
      "x-idempotency-key": idempotencyKey,
    },
    body: JSON.stringify(body),
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let detail: MintImageUploadUrlError = {
      code: "IMAGE_UPLOAD_URL_MINT_FAILED",
      message: `Mint failed (HTTP ${response.status})`,
    };
    try {
      const raw = await response.json();
      if (raw?.error) {
        detail = { ...raw.error };
      }
    } catch {
      // Body not JSON.
    }
    throw detail;
  }
  const envelope = (await response.json()) as {
    data: MintImageUploadUrlResponse;
  };
  return envelope.data;
}

export function useMintImageUploadUrl() {
  return useMutation<
    MintImageUploadUrlResponse,
    MintImageUploadUrlError,
    MintImageUploadUrlRequest
  >({
    mutationFn: (body) => postMint(body, crypto.randomUUID()),
  });
}
